import React, { useState } from 'react';
import { useAuth } from '../../AuthContext';
import './PracticeSurveys.css';

function PhilippinesSurvey() {
  // State variables to manage user answers, current question index, and form submission state
  const [answers, setAnswers] = useState({
    continent: '',
    flagStarsAndSun: '',
    capital: '',
    largestCity: '',
    totalArea: '',
    officialLanguages: '',
    nationalCurrency: '',
    independenceDay: '',
    mostFollowedReligion: '',
    naturalSymbols: '',
    nationalDishIngredients: '',
    nationalSport: '',
    majorRivers: '',
    islandsCount: '',
    maritimeBorders: '',
    president: ''
  });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [submitted, setSubmitted] = useState(false); // State to track form submission
  const { userDetails } = useAuth(); // Use userDetails from AuthContext
  const apiUrl = process.env.REACT_APP_API_BASE_URL;


  // Function to handle input change for each question
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setAnswers(prevAnswers => ({
      ...prevAnswers,
      [name]: value
    }));
  };

  // Function to handle moving to the next question
  const handleNextQuestion = () => {
    setCurrentQuestion(prevQuestion => prevQuestion + 1);
  };

  // Function to handle moving to the previous question
  const handlePreviousQuestion = () => {
    setCurrentQuestion(prevQuestion => prevQuestion - 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const finalScore = calculateScore(answers);
    setSubmitted(true);

    // Ensure that the user is logged in before allowing score submission
    if (!userDetails.email) {
      alert("You must be logged in to submit scores.");
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/submit-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userDetails.email, // Use email for score submission
          score: finalScore,
          surveyId: 'PhilippinesSurvey',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit score');
      }

      alert('Score submitted successfully!');
    } catch (error) {
      console.error('Score submission error:', error);
      alert(error.message);
    }
  };

  const calculateScore = (answers) => {
    let score = 0;
    questions.forEach(question => {
      if (answers[question.id] === question.correctAnswer) {
        score += 10;  // Assuming each correct answer gives 10 points
      }
    });
    return score;
  };

  // Questions data for the Philippines
  const questions = [
    {
      id: 'continent',
      label: 'In which continent is the Philippines located?',
      correctAnswer: 'Asia',
      options: ['Asia', 'Europe', 'North America', 'South America', 'Africa']
    },
    {
      id: 'flagStarsAndSun',
      label: 'How many star(s) and sun(s) does the Philippines flag have?',
      correctAnswer: '3 stars and 1 sun',
      options: ['1 star and 1 sun', '2 stars and 1 sun', '3 stars and 1 sun', '4 stars and 1 sun', '5 stars and 1 sun']
    },
    {
      id: 'capital',
      label: 'What is the capital of the Philippines?',
      correctAnswer: 'Manila',
      options: ['Cebu City', 'Manila', 'Davao City', 'Quezon City', 'Makati City']
    },
    {
      id: 'largestCity',
      label: 'What is the largest city in the country?',
      correctAnswer: 'Quezon City',
      options: ['Manila', 'Cebu City', 'Davao City', 'Quezon City', 'Makati City']
    },
    {
      id: 'totalArea',
      label: 'What is the total area of the Philippines?',
      correctAnswer: '343,448 SquareKM',
      options: ['250,000 SquareKM', '300,000 SquareKM', '325,000 SquareKM', '343,448 SquareKM', '400,000 SquareKM']
    },
    {
      id: 'officialLanguages',
      label: 'What are the official languages of the Philippines?',
      correctAnswer: 'Filipino and English',
      options: ['Filipino and English', 'Filipino and Spanish', 'Filipino, English, and Spanish', 'Filipino, English, and Chinese', 'Filipino, English, Spanish, and Chinese']
    },
    {
      id: 'nationalCurrency',
      label: 'What\'s the name of the national currency?',
      correctAnswer: 'Philippine peso',
      options: ['Philippine peso', 'Philippines dollar', 'Peso', 'Pilipino peso', 'Filipino dollar']
    },
    {
      id: 'independenceDay',
      label: 'When is the Independence Day of the Philippines?',
      correctAnswer: 'June 12, 1898',
      options: ['July 4, 1946', 'June 12, 1898', 'August 15, 1945', 'July 4, 1776', 'June 12, 1945']
    },
    {
      id: 'mostFollowedReligion',
      label: 'What is the most followed religion of the country?',
      correctAnswer: 'Christianity',
      options: ['Islam', 'Buddhism', 'Hinduism', 'Christianity', 'Catholicism']
    },
    {
      id: 'naturalSymbols',
      label: 'What are the three natural symbols?',
      correctAnswer: 'Sampaguita, Philippines Eagle, Narra tree',
      options: ['Tarsier, Rafflesia, Philippine Eagle', 'Narra tree, Rafflesia, Sampaguita', 'Philippine Eagle, Sampaguita, Tarsier', 'Sampaguita, Philippines Eagle, Narra tree', 'Rafflesia, Tarsier, Philippine Eagle']
    },
    {
      id: 'nationalDishIngredients',
      label: 'What are the ingredients of the national dish?',
      correctAnswer: 'Lechon, adobo, sisig, sinigang, pancit',
      options: ['Adobo, Lechon, Sinigang, Pancit', 'Sisig, Sinigang, Pancit, Lechon', 'Sinigang, Lechon, Adobo, Pancit', 'Lechon, Adobo, Sisig, Sinigang, Pancit', 'Pancit, Lechon, Adobo, Sisig, Sinigang']
    },
    {
      id: 'nationalSport',
      label: 'What is the national sport?',
      correctAnswer: 'Arnis',
      options: ['Basketball', 'Boxing', 'Arnis', 'Football', 'Sepak Takraw']
    },
    {
      id: 'majorRivers',
      label: 'What are the major rivers of the country?',
      correctAnswer: 'Cagayan River, Agusa River and Mindanao River.',
      options: ['Pasig River, Cagayan River, and Agusan River', 'Pasig River, Agusan River, and Mindanao River', 'Cagayan River, Agusan River, and Mindanao River', 'Pasig River, Cagayan River, and Mindanao River', 'Cagayan River, Agusa River, and Mindanao River']
    },
    {
      id: 'islandsCount',
      label: 'How many islands does the Philippines have?',
      correctAnswer: '7,641',
      options: ['5,000', '7,107', '7,641', '10,000', '8,000']
    },
    {
      id: 'maritimeBorders',
      label: 'Which countries share maritime borders with the Philippines?',
      correctAnswer: 'Taiwan, Japan, Palau, Indonesia, Malaysia, Vietnam, China',
      options: ['Taiwan, Japan, Palau, Indonesia, Malaysia, Vietnam, China', 'Taiwan, Japan, Indonesia, Malaysia, Vietnam, China', 'Taiwan, Japan, Palau, Malaysia, Vietnam, China', 'Taiwan, Japan, Indonesia, Vietnam, China', 'Taiwan, Japan, Palau, Indonesia, Vietnam, China']
    },
    {
      id: 'president',
      label: 'Who is the current president of the country?',
      correctAnswer: 'Bongbong Marcos',
      options: ['Rodrigo Duterte', 'Bongbong Marcos', 'Benigno Aquino III', 'Gloria Macapagal-Arroyo', 'Joseph Estrada']
    }
  ];

  return (
    <div className="container">
      {!submitted && (
        <div className="card mt-4">
          <div className="surveycard">
            <h2>Philippines Survey</h2>
          </div>
        </div>
      )}
      {!submitted && (
        <div className="card mt-4">
          <div className="surveycard">
            <form onSubmit={handleSubmit}>
              {/* Display current question */}
              <div>
                <div className="surveyform">
                  <label htmlFor={questions[currentQuestion].id}>{questions[currentQuestion].label}</label>
                  <select
                    className="surveyformcontrol"
                    id={questions[currentQuestion].id}
                    name={questions[currentQuestion].id}
                    value={answers[questions[currentQuestion].id]}
                    onChange={handleInputChange}
                  >
                    <option value="">Select an option</option>
                    {questions[currentQuestion].options.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                {/* Hide buttons after form submission */}
                {!submitted && (
                  <div>
                    {/* Next and Previous buttons */}
                    {currentQuestion > 0 && <button type="button" className="surveybuttons" onClick={handlePreviousQuestion}>Previous</button>}
                    {currentQuestion < questions.length - 1 && <button type="button" className="surveybuttons" onClick={handleNextQuestion}>Next</button>}
                    {currentQuestion === questions.length - 1 && <button type="submit" className="surveybuttons">Submit</button>}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Display answers after form submission */}
      {submitted && (
        <div className="card mt-4">
          <div className="surveycard">
            <h3 className="surveyanswers">Answers:</h3>
            {questions.map(question => (
              <div key={question.id}>
                <p><strong>{question.label}</strong></p>
                <p>Your answer: {answers[question.id]}</p>
                <p>Correct answer: {question.correctAnswer}</p>
                {/* Add logic here to compare user's answer with correct answer and style accordingly */}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhilippinesSurvey;
